import { Badge } from "@/shared/ui/core/Badge";
import type { SemanticTone } from "@/shared/ui/core/types";
import { statusLabel } from "@/shared/ui/statusLabels";
import { resolveKpiTone } from "@/shared/ui/core/statusTone";

/**
 * Shared status → Badge tone.
 * Defaults must not be `primary` (brand green) — that misreads Fail/Hold as OK.
 * Aligns with proto `lotQcTone` / `lotInventoryTone` + commercial workflow.
 */
const STATUS_TONE: Record<string, SemanticTone> = {
  // Master / boolean
  active: "success",
  enabled: "success",
  true: "success",
  inactive: "neutral",
  disabled: "neutral",
  locked: "danger",
  false: "neutral",

  // Lot QC / inspection result
  passed: "success",
  pass: "success",
  failed: "danger",
  fail: "danger",
  conditional: "warning",
  pending: "draft", // Chờ, nháp -> màu nền nhạt của màu xám (draft)

  // Lot inventory
  available: "success",
  reserved: "info",
  hold: "danger",
  quarantine: "warning",
  blocked: "danger",
  expired: "danger",
  soon: "warning",
  disposed: "danger",
  consumed: "neutral",

  // QA release decisions
  release: "success",
  danha: "success",
  dago: "success",
  đãnhả: "success",
  đãgỡ: "success",
  reject: "danger",

  // NCR / deviation
  open: "warning",
  dispositionpending: "warning",
  closed: "success",
  cancelled: "danger", // Đã hủy -> màu nóng
  canceled: "danger",

  // Commercial / txn / MO / Demand
  total: "neutral", // Tổng, tất cả -> màu xám đậm (neutral)
  all: "neutral",
  draft: "draft", // Draft, nháp -> màu nền nhạt của màu xám (draft)
  nhap: "draft",
  tamluu: "draft",
  waiting: "draft",
  published: "success",
  retired: "warning",
  confirmed: "success",
  posted: "success",
  onhold: "danger",
  partiallyreceived: "info",
  received: "success",
  shortage: "danger",
  ready: "success",
  /** MO Đã phát hành — info (xanh dương), cùng nhóm KPI «Đang thực hiện». */
  released: "info",
  daphathanh: "info",
  /** Batch: Đã cấp phát — xanh lá tích cực (success). */
  issued: "success",
  dacapphat: "success",
  /** PLN/MES material-issue coverage (đồng bộ KPI cấp phát NVL). */
  notissued: "info",
  chuacapphat: "info",
  /** FIN Partial «Thu một phần» — info (xanh dương), đồng bộ KPI. MES «Cấp một phần» override bằng label. */
  partial: "info",
  capmotphan: "danger",
  nobatches: "draft",
  chuacome: "draft",
  dacapdu: "success",
  /** Demand «Đã lên kế hoạch» = info (đồng bộ KPI filter). Batch «Chờ cấp phát» override qua label. */
  planned: "info",
  dalenkehoach: "info",
  dakh: "info",
  scheduled: "info",
  chocapphat: "warning", // Batch chờ cấp phát NVL
  inprogress: "info", // Đang chạy / đang SX
  inproduction: "info",
  dangsanxuat: "info",
  running: "info",
  wip: "info",
  processing: "info",
  paused: "warning",
  completed: "success",
  hoantat: "success",
  dahoantat: "success",
  rejected: "danger",
  submitted: "info",
  pendingapproval: "info",
  choduyet: "info",
  chosx: "draft",
  chokehoach: "draft", // Chờ kế hoạch -> màu draft (xám nhạt) đồng bộ với KpiCard
  approved: "success",
  effective: "success",
  obsolete: "danger",
  hethieuluc: "danger",
  superseded: "neutral",
  dathaythe: "neutral",
  converted: "success",
  void: "danger",
  done: "success",

  // Email message log
  simulated: "neutral",
  sent: "success",

  // Notifications / read status
  read: "success",
  unread: "danger",
  đãđọc: "success",
  dadoc: "success",
  chưađọc: "danger",
  chuadoc: "danger",

  // Severity / Priority
  critical: "danger",
  high: "danger",
  major: "danger",
  medium: "warning",
  low: "neutral",
  minor: "neutral",
};

/**
 * Phân giải tone màu cho status dựa vào KpiCard & Design System chuẩn.
 * Đồng bộ 100% giữa StatusBadge và KpiCard.
 */
export function resolveStatusTone(status: string, label?: string): SemanticTone {
  const displayLabel = label ?? statusLabel(status);
  const key = status.trim().toLowerCase().replace(/[\s_-]+/g, "");
  const baseTone = STATUS_TONE[key];

  return resolveKpiTone({
    id: status,
    label: displayLabel,
    tone: baseTone,
  });
}

/** Status → Badge tone + VI label (override with `label` for Field shorten). */
export function StatusBadge({
  status,
  label,
  tone,
  variant = "soft",
  icon,
}: {
  status: string;
  /** Display label; defaults to `statusLabel(status)`. */
  label?: string;
  /** Overrides the shared status→tone map for one badge. */
  tone?: SemanticTone;
  /** Style variant: 'soft' (default pastel) or 'solid' (saturated color + white text). */
  variant?: "soft" | "solid";
  icon?: React.ReactNode;
}) {
  const displayLabel = label ?? statusLabel(status);
  const resolvedTone = tone ?? resolveStatusTone(status, displayLabel);

  return (
    <Badge
      tone={resolvedTone}
      label={displayLabel}
      variant={variant}
      icon={icon}
    />
  );
}
